import {
  Button,
  Center,
  ChakraProps,
  Divider,
  Input,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useDisclosure,
  useTheme,
} from "@chakra-ui/react";
import { Ref, forwardRef, useRef, useState } from "react";
import {
  DayPickerSingleProps,
  DayPicker as ReactDayPicker,
} from "react-day-picker";
import "react-day-picker/dist/style.css";
import { de } from "date-fns/locale";
import { formatDate, getLocalDate } from "../utils";

export const selectDate = "Wähle ein Datum";

const DayPicker = ({ ...props }: DayPickerSingleProps) => {
  return <ReactDayPicker {...props} />;
};

type DateSelectorProps = {
  defaultValue: Date | undefined;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  isDisabled?: boolean;
} & ChakraProps;

const DateSelector = forwardRef(
  (
    {
      defaultValue,
      onChange,
      width,
      placeholder = selectDate,
      isDisabled,
      ...props
    }: DateSelectorProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const { onOpen, onClose, isOpen } = useDisclosure();
    const theme = useTheme();
    // Same accent as DateRangeSelector — without it react-day-picker's
    // default blue shows and the two calendars look unrelated.
    const accentColor = theme.colors.gray[700];
    const [date, setDate] = useState<Date | undefined>(defaultValue);
    const inputRefFallback = useRef<HTMLInputElement>(null);
    const inputRef = ref ?? inputRefFallback;

    const handleDateSelect = (selectedDate: Date | undefined) => {
      const localDate = selectedDate ? getLocalDate(selectedDate) : undefined;
      setDate(localDate);
      if (onChange) {
        onChange(localDate);
      }
      onClose();
    };

    return (
      <Popover
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        placement="bottom"
        {...props}
      >
        <PopoverTrigger>
          <Input
            ref={inputRef}
            isReadOnly={true}
            value={
              date ? formatDate(getLocalDate(new Date(date))) : placeholder
            }
            width={width}
            isDisabled={isDisabled}
            cursor={isDisabled ? undefined : "pointer"}
            onClick={(event) => {
              if (isDisabled) {
                event.preventDefault();
                return;
              }
              // Safari does not focus a readonly input on click. Without focus
              // inside the trigger, the popover's closeOnBlur fires right after
              // opening — the calendar flashes and closes. No-op elsewhere.
              event.currentTarget.focus();
            }}
          />
        </PopoverTrigger>
        <Portal>
          {/* The popper wrapper defaults to zIndex 10 and creates the stacking
              context, so a zIndex on PopoverContent alone does nothing — inside
              a Modal (1400) the calendar would render behind it. rootProps
              targets the wrapper. Matches the Select menu portal at 1500. */}
          {/* No FocusLock and no initialFocus: focus must stay on the trigger.
              Moving it into the body-portaled calendar makes the surrounding
              Modal's focus trap pull it back (Safari), which blurs the popover
              and closes it the moment it opens. The portaled Select menus in
              the same modal survive for exactly this reason — they never move
              focus. Mouse selection needs no focus in the calendar. */}
          <PopoverContent p={5} rootProps={{ zIndex: 1500 }}>
            <PopoverArrow />
            <DayPicker
              mode="single"
              locale={de}
              defaultMonth={date}
              selected={date}
              onSelect={handleDateSelect}
              captionLayout="dropdown-buttons"
              fromYear={2024}
              toYear={new Date().getFullYear()}
              modifiersStyles={{
                selected: { backgroundColor: accentColor },
              }}
            />
            <Divider borderColor="gray.200" w="full" />
            {/* Same shortcut row as the range picker's presets. */}
            <Center>
              <Button
                variant="ghost"
                color="gray.700"
                size="sm"
                fontWeight="normal"
                onClick={() => handleDateSelect(new Date())}
              >
                Heute
              </Button>
            </Center>
          </PopoverContent>
        </Portal>
      </Popover>
    );
  }
);

export default DateSelector;
